import Phaser from 'phaser'

import type { BombRainHazard, BombRainPlayer, BombRainState } from '@shared/games/protocol'

const SKIN_ACCENT = [
  '#fde68a',
  '#fca5a5',
  '#86efac',
  '#93c5fd',
  '#f0abfc',
  '#fdba74',
  '#fecdd3',
  '#bbf7d0',
  '#c7d2fe',
  '#fef08a',
  '#fda4af',
  '#a5f3fc',
]
const LERP = 0.32

interface GoofTraits {
  body: number
  headScale: number
  eye: number
  mouth: number
  hair: number
  nose: number
  ear: number
  squat: number
}

interface PlayerView {
  container: Phaser.GameObjects.Container
  body: Phaser.GameObjects.Graphics
  label: Phaser.GameObjects.Text
  targetX: number
  targetY: number
  skin: number
  // Сервер гарантирует string по типу, но на практике может прийти undefined/null
  color: string | undefined | null
  alive: boolean
  facing: number
  animT: number
  animBucket: number
  name: string
  vx: number
  localT: number
  traits: GoofTraits
}

interface HazardView {
  container: Phaser.GameObjects.Container
  gfx: Phaser.GameObjects.Graphics
  targetX: number
  targetY: number
  targetRotation: number
  type: BombRainHazard['type']
  w: number
  h: number
}

function hashKey(key: string): number {
  let h = 2166136261
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return Math.abs(h >>> 0)
}

function goofTraits(playerKey: string, skin: number): GoofTraits {
  const s = hashKey(playerKey) ^ (skin * 9973)
  return {
    body: s % 5,
    headScale: 0.85 + ((s >>> 3) % 7) * 0.07,
    eye: (s >>> 6) % 5,
    mouth: (s >>> 9) % 5,
    hair: (s >>> 12) % 6,
    nose: (s >>> 15) % 4,
    ear: (s >>> 17) % 3,
    squat: 0.85 + ((s >>> 19) % 5) * 0.06,
  }
}

// Данные игрока приходят по сети — типы TS не гарантируют валидность реального
// payload (например, если бэкенд ещё не проставил цвет для только что созданного
// игрока). При невалидном hex откатываемся на серый цвет вместо падения рендера.
// Экспортируется только для юнит-тестирования защиты от невалидного hex с сервера.
export function hexToColor(hex: string | undefined | null): number {
  if (typeof hex !== 'string' || !hex) return 0x94a3b8
  try {
    return Phaser.Display.Color.HexStringToColor(hex).color
  } catch {
    return 0x94a3b8
  }
}

function paintGoofyPlayer(
  g: Phaser.GameObjects.Graphics,
  view: {
    color: string | undefined | null
    skin: number
    animT: number
    localT: number
    vx: number
    traits: GoofTraits
  },
) {
  const t = view.traits
  const panic = Math.abs(view.vx) > 18
  const wave = panic ? view.localT * 18 : view.animT
  const bounce = panic ? Math.abs(Math.sin(wave * 1.7)) * 4 : Math.sin(wave) * 1.2
  const wobble = panic ? Math.sin(wave * 2.2) * 3 : Math.sin(wave * 0.6) * 1.5
  const legA = Math.sin(wave * (panic ? 2.4 : 1)) * (panic ? 11 : 5)
  const legB = -legA
  const armFlail = panic ? Math.sin(wave * 3.1) * 16 : Math.sin(wave * 0.8) * 4
  const armFlail2 = panic ? Math.cos(wave * 2.7) * 14 : Math.cos(wave * 0.7) * 3

  // Number.isFinite + приведение к неотрицательному остатку защищают от отрицательного/NaN
  // skin с сервера — иначе индексация массива даёт undefined.
  const safeSkin = Number.isFinite(view.skin) ? Math.abs(view.skin) : 0
  const main = hexToColor(view.color)
  const accent = hexToColor(SKIN_ACCENT[safeSkin % SKIN_ACCENT.length])
  const skinTone = hexToColor(
    ['#fde68a', '#fdba74', '#fca5a5', '#fef3c7', '#fcd34d', '#fb923c'][safeSkin % 6],
  )

  g.save()
  g.translateCanvas(wobble, -bounce)

  g.fillStyle(0x000000, 0.22)
  g.fillEllipse(0, 2, 28 * t.squat, 8)

  g.fillStyle(0x1e293b, 1)
  g.fillRoundedRect(-13 * t.squat, -6, 11, 12 + Math.max(0, legA), 4)
  g.fillRoundedRect(2 * t.squat, -6, 11, 12 + Math.max(0, legB), 4)
  g.fillStyle(0x0f172a, 1)
  g.fillRoundedRect(-14 * t.squat, 4 + Math.max(0, legA), 13, 6, 3)
  g.fillRoundedRect(1 * t.squat, 4 + Math.max(0, legB), 13, 6, 3)

  g.fillStyle(main, 1)
  if (t.body === 0) g.fillEllipse(0, -24 * t.squat, 38 * t.squat, 36 * t.squat)
  else if (t.body === 1)
    g.fillRoundedRect(-16 * t.squat, -42 * t.squat, 32 * t.squat, 40 * t.squat, 14)
  else if (t.body === 2) {
    g.fillEllipse(0, -20 * t.squat, 42 * t.squat, 28 * t.squat)
    g.fillEllipse(0, -34 * t.squat, 28 * t.squat, 26 * t.squat)
  } else if (t.body === 3)
    g.fillRoundedRect(-20 * t.squat, -40 * t.squat, 40 * t.squat, 38 * t.squat, 8)
  else g.fillRoundedRect(-11 * t.squat, -46 * t.squat, 22 * t.squat, 44 * t.squat, 9)

  g.fillStyle(accent, 0.9)
  g.fillCircle(-4, -28 * t.squat, 3)
  g.fillCircle(5, -22 * t.squat, 2.5)

  g.lineStyle(6, main, 1)
  g.beginPath()
  g.moveTo(-14 * t.squat, -32 * t.squat)
  g.lineTo(-22 * t.squat - armFlail, -40 * t.squat - Math.abs(armFlail) * 0.4)
  g.strokePath()
  g.beginPath()
  g.moveTo(14 * t.squat, -32 * t.squat)
  g.lineTo(22 * t.squat + armFlail2, -38 * t.squat - Math.abs(armFlail2) * 0.35)
  g.strokePath()
  g.fillStyle(skinTone, 1)
  g.fillCircle(-22 * t.squat - armFlail, -40 * t.squat - Math.abs(armFlail) * 0.4, 5)
  g.fillCircle(22 * t.squat + armFlail2, -38 * t.squat - Math.abs(armFlail2) * 0.35, 5)

  if (t.ear > 0) {
    g.fillStyle(skinTone, 1)
    const earW = t.ear === 2 ? 8 : 5
    g.fillEllipse(-18 * t.headScale, -52 * t.headScale, earW, earW + 2)
    g.fillEllipse(18 * t.headScale, -52 * t.headScale, earW, earW + 2)
  }

  const hx = 0
  const hy = -52 * t.headScale
  const hr = 15 * t.headScale
  g.fillStyle(skinTone, 1)
  g.fillCircle(hx, hy, hr)

  g.fillStyle(accent, 1)
  if (t.hair === 0) {
    for (let i = -3; i <= 3; i += 1) {
      g.fillTriangle(
        hx + i * 4,
        hy - hr + 4,
        hx + i * 4 - 3,
        hy - hr - 8 - (i % 2) * 4,
        hx + i * 4 + 3,
        hy - hr - 6,
      )
    }
  } else if (t.hair === 1) {
    g.fillEllipse(hx + 2, hy - hr + 2, 10, 6)
    g.fillTriangle(hx - 2, hy - hr, hx, hy - hr - 12, hx + 4, hy - hr)
  } else if (t.hair === 2) {
    g.fillCircle(hx - 8, hy - hr + 2, 7)
    g.fillCircle(hx, hy - hr - 2, 8)
    g.fillCircle(hx + 8, hy - hr + 2, 7)
  } else if (t.hair === 3) {
    g.lineStyle(2, 0x64748b, 1)
    g.lineBetween(hx, hy - hr, hx + 2, hy - hr - 14)
    g.fillStyle(0xef4444, 1)
    g.fillCircle(hx + 2, hy - hr - 14, 3)
  } else if (t.hair === 4) {
    g.fillEllipse(hx - 4, hy - hr + 4, 18, 10)
  }

  const eyeY = hy - 2
  const eyeSpread = 5 + (t.eye % 2)
  if (t.eye === 0) {
    g.fillStyle(0xffffff, 1)
    g.fillCircle(hx - eyeSpread, eyeY, 4.5)
    g.fillCircle(hx + eyeSpread + 1, eyeY + 1, 5.5)
    g.fillStyle(0x0f172a, 1)
    g.fillCircle(hx - eyeSpread - 1, eyeY + 1, 2)
    g.fillCircle(hx + eyeSpread + 2, eyeY, 2.5)
  } else if (t.eye === 1) {
    g.fillStyle(0xffffff, 1)
    g.fillCircle(hx - eyeSpread, eyeY, 4)
    g.fillCircle(hx + eyeSpread, eyeY, 4)
    g.fillStyle(0x0f172a, 1)
    g.fillCircle(hx - eyeSpread + 1, eyeY, 2)
    g.fillCircle(hx + eyeSpread + 1, eyeY, 2)
    g.lineStyle(2, 0x0f172a, 1)
    g.lineBetween(hx - eyeSpread - 4, eyeY - 5, hx - eyeSpread + 3, eyeY - 2)
    g.lineBetween(hx + eyeSpread + 4, eyeY - 5, hx + eyeSpread - 3, eyeY - 2)
  } else if (t.eye === 2) {
    g.lineStyle(2, 0x0f172a, 1)
    g.lineBetween(hx - eyeSpread - 3, eyeY, hx - eyeSpread + 3, eyeY)
    g.lineBetween(hx + eyeSpread - 3, eyeY + 1, hx + eyeSpread + 3, eyeY - 1)
  } else if (t.eye === 3) {
    g.fillStyle(0xffffff, 1)
    g.fillCircle(hx - eyeSpread, eyeY, 3)
    g.fillCircle(hx + eyeSpread, eyeY - 1, 6)
    g.fillStyle(0xdc2626, 1)
    g.fillCircle(hx - eyeSpread, eyeY, 1.5)
    g.fillStyle(0x0f172a, 1)
    g.fillCircle(hx + eyeSpread + 1, eyeY, 3)
  } else {
    g.fillStyle(0xffffff, 1)
    g.fillCircle(hx - eyeSpread, eyeY, 5)
    g.fillCircle(hx + eyeSpread, eyeY, 5)
    g.fillStyle(0x0f172a, 1)
    g.fillCircle(hx - eyeSpread, eyeY - 2, 2.2)
    g.fillCircle(hx + eyeSpread, eyeY - 2, 2.2)
  }

  if (t.nose === 1) {
    g.fillStyle(0xfb7185, 1)
    g.fillCircle(hx, hy + 3, 3)
  } else if (t.nose === 2) {
    g.fillStyle(0xf97316, 1)
    g.fillEllipse(hx + 1, hy + 4, 8, 5)
  } else if (t.nose === 3) {
    g.lineStyle(2, 0x0f172a, 1)
    g.lineBetween(hx, hy, hx + 3, hy + 5)
  }

  g.lineStyle(2, 0x0f172a, 1)
  if (t.mouth === 0) g.strokeCircle(hx, hy + 8, 3)
  else if (t.mouth === 1) {
    g.beginPath()
    g.arc(hx, hy + 6, 5, 0.2, Math.PI - 0.2, false)
    g.strokePath()
  } else if (t.mouth === 2) {
    g.beginPath()
    g.arc(hx, hy + 10, 5, Math.PI + 0.2, -0.2, false)
    g.strokePath()
  } else if (t.mouth === 3) {
    g.fillStyle(0x0f172a, 1)
    g.fillEllipse(hx, hy + 8, 8, 5)
    g.fillStyle(0xf472b6, 1)
    g.fillEllipse(hx, hy + 9, 4, 2)
  } else {
    g.fillStyle(0x0f172a, 1)
    g.fillEllipse(hx + 1, hy + 8, 6, panic ? 8 : 4)
  }

  g.fillStyle(0xfb7185, 0.45)
  g.fillCircle(hx - 10 * t.headScale, hy + 4, 3)
  g.fillCircle(hx + 10 * t.headScale, hy + 4, 3)

  if (panic) {
    g.fillStyle(0x7dd3fc, 0.9)
    const sweatY = hy - hr - 4 - (wave % 3)
    g.fillCircle(hx + 14, sweatY, 2.5)
    g.fillCircle(hx + 18, sweatY + 5, 2)
    g.lineStyle(2, 0xfef08a, 0.8)
    g.lineBetween(hx - 20, hy - 18, hx - 26, hy - 26)
    g.lineBetween(hx - 16, hy - 22, hx - 20, hy - 30)
  }

  g.restore()
}

class BombRainSpectatorScene extends Phaser.Scene {
  private pendingState: BombRainState | null = null
  private stars: Phaser.GameObjects.Arc[] = []
  private playerViews = new Map<string, PlayerView>()
  private hazardViews = new Map<string, HazardView>()
  private hudText?: Phaser.GameObjects.Text
  private winnerBanner?: Phaser.GameObjects.Text
  private bgGfx?: Phaser.GameObjects.Graphics
  private floorGfx?: Phaser.GameObjects.Graphics
  private lastHudKey = ''
  private speedMultiplier = 1

  constructor() {
    super('BombRainSpectatorScene')
  }

  create() {
    this.cameras.main.setBackgroundColor('transparent')
    for (let i = 0; i < 60; i += 1) {
      const star = this.add
        .circle(
          Phaser.Math.Between(0, 960),
          Phaser.Math.Between(0, 420),
          Phaser.Math.FloatBetween(0.6, 2),
          0xffffff,
          Phaser.Math.FloatBetween(0.2, 0.65),
        )
        .setDepth(1)
      this.stars.push(star)
    }
    this.bgGfx = this.add.graphics().setDepth(0)
    this.floorGfx = this.add.graphics().setDepth(2)
    this.hudText = this.add
      .text(16, 12, '', {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: 'Segoe UI, sans-serif',
      })
      .setDepth(100)
    this.winnerBanner = this.add
      .text(480, 270, '', {
        fontSize: '72px',
        color: '#c4b5fd',
        fontFamily: 'Segoe UI, sans-serif',
        align: 'center',
        stroke: '#1e1b4b',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setDepth(200)
      .setVisible(false)

    if (this.pendingState) {
      const s = this.pendingState
      this.pendingState = null
      this.applyState(s)
    }
  }

  update() {
    // update() вызывается движком Phaser внутри своего RAF-цикла, React ErrorBoundary
    // и обёртка в GamesLayer такую ошибку не ловят. Без try/catch падение здесь
    // остановит весь game loop навсегда, даже после прихода корректного состояния.
    try {
      this.tickPlayers()
      this.tickHazards()
      this.tickStars()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[overlay/bomb-rain] update() failed, skipping frame:', error)
    }
  }

  private tickPlayers() {
    for (const view of this.playerViews.values()) {
      view.container.x = Phaser.Math.Linear(view.container.x, view.targetX, LERP)
      view.container.y = Phaser.Math.Linear(view.container.y, view.targetY, LERP)
      if (view.alive) {
        view.localT += 1 / 30
        const panicking = Math.abs(view.vx) > 18
        const bucket = panicking
          ? Math.floor(view.localT * 20)
          : Math.floor(view.animT * 8 + view.localT * 2)
        if (bucket !== view.animBucket) {
          view.animBucket = bucket
          this.drawPlayerBody(view)
        }
      }
    }
  }

  private tickHazards() {
    for (const view of this.hazardViews.values()) {
      view.container.x = Phaser.Math.Linear(view.container.x, view.targetX, LERP)
      view.container.y = Phaser.Math.Linear(view.container.y, view.targetY, LERP)
      view.container.rotation = Phaser.Math.Angle.RotateTo(
        view.container.rotation,
        view.targetRotation,
        0.25,
      )
    }
  }

  private tickStars() {
    for (const star of this.stars) {
      star.y += 0.08 * this.speedMultiplier
      if (star.y > 420) star.y = -4
    }
  }

  applyState(state: BombRainState) {
    if (!this.sys?.isActive()) {
      this.pendingState = state
      return
    }
    this.syncFromServer(state)
  }

  clearAll() {
    for (const view of this.playerViews.values()) view.container.destroy(true)
    for (const view of this.hazardViews.values()) view.container.destroy(true)
    this.playerViews.clear()
    this.hazardViews.clear()
    this.winnerBanner?.setVisible(false)
    this.lastHudKey = ''
  }

  private drawBackground(state: BombRainState) {
    if (!this.bgGfx || !this.floorGfx) return
    const g = this.bgGfx
    g.clear()
    g.fillGradientStyle(0x0f172a, 0x0f172a, 0x312e81, 0x7c3aed, 0.92)
    g.fillRect(0, 0, state.arena.width, state.arena.height)
    const floor = this.floorGfx
    floor.clear()
    floor.fillStyle(0x0b1024, 0.85)
    floor.fillRect(0, 430, state.arena.width, 110)
    floor.lineStyle(2, 0x38bdf8, 0.55)
    floor.lineBetween(0, 430, state.arena.width, 430)
  }

  private syncFromServer(state: BombRainState) {
    this.speedMultiplier = state.speedMultiplier
    this.drawBackground(state)
    this.scale.resize(state.arena.width, state.arena.height)

    const remainMs = Math.max(0, (state.roundDurationMs ?? 180_000) - state.elapsedMs)
    const countdownLeft = state.countdownEndsAt
      ? Math.max(0, Math.ceil((state.countdownEndsAt - Date.now()) / 1000))
      : 0
    const hudKey = `${state.phase}|${state.round}|${state.speedMultiplier}|${state.aliveCount}|${Math.floor(remainMs / 1000)}|${countdownLeft}|${state.winner?.displayName ?? ''}|${state.standings?.length ?? 0}`
    if (hudKey !== this.lastHudKey) {
      this.lastHudKey = hudKey
      if (state.phase === 'lobby') {
        this.hudText?.setText(
          `Bomb Rain · Lobby · ${state.players.length} players`,
        )
        this.winnerBanner?.setVisible(false)
      } else if (state.phase === 'countdown') {
        const left = Math.max(1, countdownLeft || 3)
        this.hudText?.setText(`Round ${state.round}`)
        this.winnerBanner?.setText(`${left}`).setColor('#c4b5fd').setStroke('#1e1b4b', 5).setFontSize('72px').setVisible(true)
      } else if (state.phase === 'playing') {
        const mm = String(Math.floor(remainMs / 60000)).padStart(2, '0')
        const ss = String(Math.floor((remainMs % 60000) / 1000)).padStart(2, '0')
        this.hudText?.setText(
          `Bomb Rain R${state.round} · ${mm}:${ss} · Alive ${state.aliveCount}`,
        )
        this.winnerBanner?.setVisible(false).setFontSize('28px')
      } else if (state.phase === 'results') {
        const lines = (state.standings ?? [])
          .slice(0, 5)
          .map((row) => {
            const totalSec = Math.max(0, Math.floor(row.survivedMs / 1000))
            const m = String(Math.floor(totalSec / 60)).padStart(2, '0')
            const s = String(totalSec % 60).padStart(2, '0')
            return `${row.rank}. ${row.displayName} — ${m}:${s}`
          })
          .join('\n')
        this.hudText?.setText(`Round ${state.round}`)
        this.winnerBanner
          ?.setText(`WINNER\n${lines || state.winner?.displayName || ''}`)
          .setColor('#e9d5ff')
          .setFontSize('24px')
          .setVisible(true)
      }
    }

    const seenP = new Set<string>()
    for (const p of state.players) {
      seenP.add(p.playerKey)
      this.upsertPlayer(p)
    }
    for (const [k, view] of this.playerViews) {
      if (!seenP.has(k)) {
        view.container.destroy(true)
        this.playerViews.delete(k)
      }
    }

    const seenH = new Set<string>()
    for (const h of state.hazards) {
      seenH.add(h.id)
      this.upsertHazard(h)
    }
    for (const [id, view] of this.hazardViews) {
      if (!seenH.has(id)) {
        view.container.destroy(true)
        this.hazardViews.delete(id)
      }
    }
  }

  private upsertPlayer(player: BombRainPlayer) {
    let view = this.playerViews.get(player.playerKey)
    if (!view) {
      const container = this.add.container(player.x, player.y).setDepth(20)
      const body = this.add.graphics()
      const label = this.add
        .text(0, -78, player.displayName, {
          fontSize: '11px',
          color: '#fff',
          stroke: '#000',
          strokeThickness: 2,
        })
        .setOrigin(0.5, 1)
      container.add([body, label])
      view = {
        container,
        body,
        label,
        targetX: player.x,
        targetY: player.y,
        skin: player.skin,
        color: player.color,
        alive: player.alive,
        facing: player.facing,
        animT: player.animT,
        animBucket: -1,
        name: player.displayName,
        vx: player.vx,
        localT: Math.random() * 10,
        traits: goofTraits(player.playerKey, player.skin),
      }
      this.playerViews.set(player.playerKey, view)
      this.drawPlayerBody(view)
    }

    view.targetX = player.x
    view.targetY = player.y
    view.animT = player.animT
    view.vx = player.vx
    view.container.setAlpha(player.alive ? 1 : 0.25)
    view.container.setScale(1, 1)
    view.body.setScale(player.facing, 1)

    const needsRedraw =
      view.alive !== player.alive ||
      view.skin !== player.skin ||
      view.color !== player.color ||
      view.facing !== player.facing

    view.alive = player.alive
    view.skin = player.skin
    view.color = player.color
    view.facing = player.facing

    if (view.name !== player.displayName) {
      view.name = player.displayName
      view.label.setText(player.displayName)
    }
    if (needsRedraw) this.drawPlayerBody(view)
  }

  private drawPlayerBody(view: PlayerView) {
    const g = view.body
    g.clear()
    if (!view.alive) {
      g.fillStyle(0x94a3b8, 0.65)
      g.fillEllipse(0, -8, 36, 14)
      g.lineStyle(2, 0x0f172a, 0.8)
      g.lineBetween(-10, -12, -4, -6)
      g.lineBetween(-4, -12, -10, -6)
      g.lineBetween(4, -12, 10, -6)
      g.lineBetween(10, -12, 4, -6)
      view.label.setVisible(false)
      return
    }
    view.label.setVisible(true)
    paintGoofyPlayer(g, view)
  }

  private upsertHazard(hazard: BombRainHazard) {
    let view = this.hazardViews.get(hazard.id)
    if (!view) {
      const container = this.add.container(hazard.x, hazard.y).setDepth(15)
      const gfx = this.add.graphics()
      container.add(gfx)
      view = {
        container,
        gfx,
        targetX: hazard.x,
        targetY: hazard.y,
        targetRotation: hazard.rotation,
        type: hazard.type,
        w: hazard.w,
        h: hazard.h,
      }
      this.hazardViews.set(hazard.id, view)
      this.drawHazard(view)
    }

    view.targetX = hazard.x
    view.targetY = hazard.y
    view.targetRotation = hazard.rotation
    if (view.type !== hazard.type || view.w !== hazard.w || view.h !== hazard.h) {
      view.type = hazard.type
      view.w = hazard.w
      view.h = hazard.h
      this.drawHazard(view)
    }
  }

  private drawHazard(view: HazardView) {
    const g = view.gfx
    g.clear()
    if (view.type === 'meteor') {
      g.fillStyle(0xfb923c, 1)
      g.fillCircle(0, 0, view.w / 2)
      g.lineStyle(3, 0xf97316, 0.7)
      g.strokeCircle(0, 0, view.w / 2 + 3)
    } else if (view.type === 'bomb') {
      g.fillStyle(0x1e293b, 1)
      g.fillCircle(0, 0, view.w / 2)
      g.fillStyle(0xef4444, 1)
      g.fillCircle(-4, -6, 5)
    } else {
      g.fillStyle(0x22d3ee, 0.4)
      g.fillRect(-view.w / 2, -view.h / 2, view.w, view.h)
      g.fillStyle(0xffffff, 0.95)
      g.fillRect(-3, -view.h / 2, 6, view.h)
    }
  }
}

export function createBombRainSpectator(parent: HTMLElement) {
  const scene = new BombRainSpectatorScene()
  let destroyed = false
  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent,
    width: 960,
    height: 540,
    transparent: true,
    scene: [scene],
    scale: { mode: Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_BOTH },
    fps: { target: 30 },
  })

  return {
    applyState: (state: unknown) => {
      if (!destroyed && state) scene.applyState(state as BombRainState)
    },
    clear: () => scene.clearAll(),
    destroy: () => {
      destroyed = true
      game.destroy(true)
    },
  }
}
